<template>
  <div>
    <div class="row mb-3">
      <!-- Coluna principal -->
      <div class="col-lg-8 col-12 mb-2 mb-lg-0">
        <div
          class="card mb-0 h-100"
          :style="{ borderLeft: '8px solid ' + classificationColor }"
        >
          <div class="card-body">
            <div class="row">
              <!-- Esquerda: avatar + identidade -->
              <div class="d-flex justify-content-between flex-column col-xl-6 col-12">
                <div class="d-flex justify-content-start">
                  <span
                    class="b-avatar occ-ib-avatar rounded"
                    :style="{ backgroundColor: classificationColor + '1a' }"
                  >
                    <feather-icon
                      icon="FileTextIcon"
                      size="36"
                      :style="{ color: classificationTextColor }"
                    />
                  </span>
                  <div class="d-flex flex-column ml-1">
                    <div class="mb-1">
                      <h4 class="mb-0">
                        {{ occurrence.requester_name || $t('occurrence.infoBlock.requesterNotInformed') }}
                      </h4>
                      <span class="card-text text-muted" style="font-size: 13px">
                        <feather-icon icon="PhoneIcon" size="12" class="mr-25" />
                        {{ $occurrencePhone(occurrence) || $t('occurrence.infoBlock.phoneNotInformed') }}
                      </span>
                      <br />
                      <div class="occ-ib__tags mt-50">
                        <span class="occ-tag occ-tag--discipline">
                          {{ occurrence.discipline ? occurrence.discipline.name : '—' }}
                        </span>
                        <span class="occ-tag occ-tag--type">
                          {{ occurrence.dispatch_type ? occurrence.dispatch_type.name : '—' }}
                        </span>
                        <span class="occ-tag occ-tag--reason">
                          {{ occurrence.dispatch_reason ? occurrence.dispatch_reason.name : '—' }}
                        </span>
                        <span
                          v-if="victimsBadge"
                          :class="['occ-tag', victimsBadge.class]"
                        >
                          {{ victimsBadge.label }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center mt-2">
                  <span class="b-avatar badge-light-info rounded">
                    <feather-icon icon="ClockIcon" size="18" />
                  </span>
                  <div class="ml-1">
                    <h5 class="mb-0">{{ elapsedHHMMSS(occurrence.created_at, occurrence.finalized_at) }}</h5>
                    <small>{{ $t('occurrence.infoBlock.elapsedSinceOpen') }}</small>
                  </div>
                </div>
              </div>

              <!-- Direita: tabela + KPI etapa -->
              <div class="col-xl-6 col-12">
                <table class="mt-2 mt-xl-0 w-100">
                  <tr>
                    <th class="pb-50">
                      <feather-icon icon="MapPinIcon" size="14" class="mr-75" />
                      <span class="font-weight-bold">{{ $t('occurrence.infoBlock.addressLabel') }}</span>
                    </th>
                    <td class="pb-50">
                      {{ addressLine || '—' }}
                      <span
                        v-if="occurrence.neighborhood || occurrence.city"
                        class="d-block text-muted"
                        style="font-size: 11px"
                      >
                        {{ [occurrence.neighborhood, occurrence.city, occurrence.state].filter(Boolean).join(' · ') }}
                      </span>
                      <span
                        v-if="occurrence.reference_point"
                        class="d-block text-muted"
                        style="font-size: 11px"
                      >
                        {{ $t('occurrence.infoBlock.referenceLabel') }} {{ occurrence.reference_point }}
                      </span>
                      <button
                        v-if="occurrence.latitude && occurrence.longitude"
                        class="occ-map-btn mt-25"
                        @click="showMapModal = true"
                      >
                        <feather-icon icon="MapPinIcon" size="11" />
                        {{ $t('occurrence.infoBlock.viewMap') }}
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <th class="pb-50">
                      <feather-icon icon="UserCheckIcon" size="14" class="mr-75" />
                      <span class="font-weight-bold">{{ $t('occurrence.infoBlock.openedByLabel') }}</span>
                    </th>
                    <td class="pb-50">
                      {{ occurrence.created_by ? occurrence.created_by.name : '—' }}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <feather-icon icon="UsersIcon" size="14" class="mr-75" />
                      <span class="font-weight-bold">{{ $t('occurrence.infoBlock.involvedsLabel') }}</span>
                    </th>
                    <td>
                      {{ involvedsCount }}
                      {{ involvedsCount === 1 ? $t('occurrence.infoBlock.involvedSingular') : $t('occurrence.infoBlock.involvedPlural') }}
                    </td>
                  </tr>
                </table>
                <div class="d-flex align-items-center mt-2">
                  <span :class="['b-avatar rounded', elapsedKpiBadge]">
                    <feather-icon icon="WatchIcon" size="18" />
                  </span>
                  <div class="ml-1">
                    <h5 class="mb-0">
                      {{ elapsedHHMMSS(occurrence.current_step_since || occurrence.created_at, occurrence.finalized_at) }}
                    </h5>
                    <small>{{ $t('occurrence.infoBlock.elapsedCurrentStep') }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coluna direita: protocolo + timer + datas + classificação -->
      <div class="col-lg-4 col-12">
        <div
          class="card mb-0 h-100"
          :style="{ border: '1px solid ' + classificationColor }"
        >
          <div class="card-header d-flex justify-content-between align-items-center pt-75 pb-25">
            <h5 class="mb-0" style="font-weight: bold; font-size: 20px">
              {{ occurrence.protocol || '#' + occurrence.id }}
              <b-badge v-if="isFromChildNode" variant="light-primary" class="ml-50" style="font-size: 11px; vertical-align: middle">
                {{ occurrence.node.name }}
              </b-badge>
            </h5>
            <h2
              class="occ-ib-timer-value piscar"
              :style="{ color: classificationTextColor }"
            >
              {{ elapsedHHMMSS(occurrence.current_step_since || occurrence.created_at, occurrence.finalized_at) }}
              <feather-icon icon="ClockIcon" size="22" class="ml-25 piscar" />
            </h2>
            <small class="text-muted w-100">{{ $t('occurrence.infoBlock.protocolLabel') }}</small>
          </div>
          <div class="card-body">
            <ul class="list-unstyled my-1">
              <li>
                <span class="align-middle">
                  <h5 class="mb-0" style="font-weight: bold; font-size: 14px">
                    {{ occurrence.created_at | formatDate }}
                  </h5>
                  <small class="text-muted w-100">{{ $t('occurrence.infoBlock.openedAtLabel') }}</small>
                </span>
              </li>
              <!-- Tempo-resposta: da abertura até a 1ª equipe chegar ao local. Conta (piscando, na cor
                   da classificação) enquanto ninguém chegou; depois fica fixo. -->
              <li class="mt-2">
                <span class="align-middle">
                  <h5
                    :class="['mb-0', { piscar: responseRunning }]"
                    :style="{ fontWeight: 'bold', fontSize: '14px', color: classificationTextColor }"
                  >
                    {{ responseTime }}
                  </h5>
                  <small class="text-muted w-100">{{ $t('occurrence.infoBlock.responseTimeLabel') }}</small>
                </span>
              </li>
              <li class="mt-2" v-if="occurrence.current_step_since">
                <span class="align-middle">
                  <h5 class="mb-0" style="font-weight: bold; font-size: 14px">
                    {{ occurrence.current_step_since | formatDate }}
                  </h5>
                  <small class="text-muted w-100">{{ $t('occurrence.infoBlock.currentStepSinceLabel') }}</small>
                </span>
              </li>
            </ul>

            <div
              v-if="occurrence.classification"
              class="occ-classification-badge"
              :style="{
                backgroundColor: classificationColor + '18',
                borderColor: classificationColor,
                color: classificationTextColor,
              }"
            >
              <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
              {{ occurrence.classification.color_label }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal mapa -->
    <b-modal
      v-model="showMapModal"
      :title="$t('occurrence.infoBlock.mapModalTitle')"
      size="lg"
      hide-footer
      centered
      @shown="onMapModalShown"
      @hidden="unwatchFleetPositions"
    >
      <div v-if="occurrence.latitude && occurrence.longitude">
        <p class="text-muted mb-2" style="font-size: 13px">
          <feather-icon icon="MapPinIcon" size="13" class="mr-25 text-danger" />
          {{ addressLine || $t('occurrence.infoBlock.addressNotInformed') }}
        </p>
        <GmapMap
          :center="{ lat: parseFloat(occurrence.latitude), lng: parseFloat(occurrence.longitude) }"
          :zoom="14"
          style="width: 100%; height: 420px; border-radius: 8px"
        >
          <GmapMarker
            :position="{ lat: parseFloat(occurrence.latitude), lng: parseFloat(occurrence.longitude) }"
            :icon="occurrencePinIcon(occurrence)"
            :z-index="100"
          />
          <FleetVehicleMarker
            v-for="position in fleetPositionsOnMap"
            :key="position.identifier_code"
            :position="position"
            :title="fleetMarkerTitle(position)"
            :z-index="position.stale ? 1 : 10"
          />
        </GmapMap>

        <div class="fleet-legend">
          <span><i class="fleet-dot" style="background:#28c76f"></i>{{ $t('occurrence.infoBlock.fleetLegendFree') }}</span>
          <span><i class="fleet-dot" style="background:#ff9f43"></i>{{ $t('occurrence.infoBlock.fleetLegendBusy') }}</span>
          <span><i class="fleet-dot" style="background:#b4bdc6"></i>{{ $t('occurrence.infoBlock.fleetLegendStale', { minutes: Math.round(fleetStaleAfter / 60) }) }}</span>
        </div>

        <div v-if="loadingFleetPositions" class="text-muted mt-1" style="font-size: 12px">
          {{ $t('occurrence.infoBlock.loadingFleet') }}
        </div>
        <div v-else-if="!fleetPositions.length" class="text-muted mt-1" style="font-size: 12px">
          {{ $t('occurrence.infoBlock.noFleetPositions') }}
        </div>
        <div v-else class="fleet-list">
          <div
            v-for="position in fleetPositions"
            :key="position.identifier_code"
            class="fleet-row"
            :class="{ 'fleet-row--stale': position.stale }"
          >
            <i class="fleet-dot" :style="{ background: fleetColor(position) }"></i>
            <span class="fleet-row__code">{{ position.identifier_code }}</span>
            <span class="fleet-row__team">{{ position.team ? position.team.name : $t('occurrence.infoBlock.noTeamLinked') }}</span>
            <span class="fleet-row__state">{{ fleetStateLabel(position) }}</span>
            <span class="fleet-row__age">{{ fleetAgeLabel(position) }}</span>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin.js'
import { getReadableColor } from '@/utils/color'
import FleetPositionsMixin from '@/mixins/FleetPositionsMixin.js'
import OccurrencePinMixin from '@/mixins/OccurrencePinMixin'
import FleetVehicleMarker from '@/components/FleetVehicleMarker.vue'

export default {
  name: 'OccurrenceInfoBlock',
  components: { FleetVehicleMarker },

  mixins: [ElapsedTimeMixin, FleetPositionsMixin, OccurrencePinMixin],
  filters: {
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR')
    },
  },
  props: {
    occurrence: { type: Object, required: true },
    involvedsCount: { type: Number, default: 0 },
  },
  data() {
    return {
      showMapModal: false,
    }
  },
  methods: {
    onMapModalShown() {
      this.loadFleetPositions()
      this.watchFleetPositions()
    },

  },

  computed: {
    // Logradouro completo: "Rua X, 123 - apto 4". Número e complemento só entram quando informados; o número
    // não repete se o próprio logradouro já o traz ("Rua X, 123", como o endereço do Google).
    addressLine() {
      const { address, number, complement } = this.occurrence
      const numberInAddress = !!address && !!number && new RegExp(`(^|\\D)${String(number).trim()}(\\D|$)`).test(address)
      const street = [address, numberInAddress ? null : number].filter(Boolean).join(', ')

      return [street, complement].filter(Boolean).join(' - ')
    },

    // Ocorrência finalizada: os contadores do cabeçalho param (ver ElapsedTimeMixin).
    elapsedStopped() {
      return !!(this.occurrence && this.occurrence.finalized_at)
    },
    // Sem chegada e sem finalização, o tempo-resposta ainda está correndo.
    responseRunning() {
      return !this.occurrence.arrived_at && !this.occurrence.finalized_at
    },
    responseTime() {
      if (this.occurrence.arrived_at) return this.elapsedHHMMSS(this.occurrence.created_at, this.occurrence.arrived_at)
      // Finalizada sem equipe ter chegado (ex.: informação): não há tempo-resposta.
      if (this.occurrence.finalized_at) return '—'

      return this.elapsedHHMMSS(this.occurrence.created_at)
    },
    classificationColor() {
      return this.occurrence?.classification?.hex_color || '#7367f0'
    },
    isFromChildNode() {
      const occNodeId = this.occurrence?.node_id
      const selectedNodeId = this.$store.state.selected_node?.id
      return !!occNodeId && !!selectedNodeId && occNodeId !== selectedNodeId
    },
    // Cor de texto/ícone sobre fundo branco ou tingido claro: cores vivas
    // demais (ex: amarelo) caem pra preto em vez de ficarem ilegíveis.
    classificationTextColor() {
      return getReadableColor(this.classificationColor)
    },
    victimsBadge() {
      if (this.occurrence?.victims_status === 'none') {
        return { label: this.$t('occurrence.infoBlock.victimsNone'), class: 'occ-tag--victims-none' }
      }
      if (this.occurrence?.victims_status === 'unknown') {
        return { label: this.$t('occurrence.infoBlock.victimsUnknown'), class: 'occ-tag--victims-unknown' }
      }
      return null
    },
    elapsedKpiBadge() {
      const cls = this.elapsedBadgeClass(
        this.occurrence.current_step_since || this.occurrence.created_at,
        this.occurrence.current_step,
        this.occurrence.discipline_id,
      )
      if (cls.includes('--danger')) return 'badge-light-danger'
      if (cls.includes('--warning')) return 'badge-light-warning'
      return 'badge-light-success'
    },
  },
}
</script>

<style scoped>
.fleet-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  font-size: 12px;
  color: #6e6b7b;
}

.fleet-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.fleet-list {
  margin-top: 10px;
  border-top: 1px solid #ebe9f1;
}

.fleet-row {
  display: grid;
  grid-template-columns: 16px 90px 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 7px 2px;
  border-bottom: 1px solid #f4f3f7;
  font-size: 12.5px;
}

.fleet-row--stale { opacity: 0.55; }

.fleet-row__code { font-weight: 600; }
.fleet-row__team { color: #6e6b7b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fleet-row__state { color: #6e6b7b; }
.fleet-row__age { color: #b9b9c3; font-variant-numeric: tabular-nums; }

.occ-ib-avatar {
  width: 104px !important;
  height: 104px !important;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.occ-ib__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.occ-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.occ-tag--discipline,
.occ-tag--type,
.occ-tag--reason {
  background: #f3f2f7;
  color: #5e5873;
}

.occ-tag--victims-none {
  background: #f3f2f7;
  color: #82868b;
}

.occ-tag--victims-unknown {
  background: #fff5e6;
  color: #a15c00;
}

.occ-classification-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border: 1.5px solid;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-top: 10px;
}

.occ-map-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 12px;
  background: #f3f2f7;
  border: 1px solid #e0dfe8;
  border-radius: 20px;
  color: #7367f0;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  font-weight: 600;
}

.occ-map-btn:hover {
  background: #ede9fd;
  border-color: #7367f0;
}

.occ-ib-timer-value {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 1px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

@keyframes piscar {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}

.piscar { animation: piscar 1.5s ease-in-out infinite; }
</style>
